import React from "react";
import { View, StyleSheet } from "react-native";
import {
  UsersIcon,
  UserPlusIcon,
  UserMinusIcon,
  PlusIcon,
  PencilSimpleIcon,
  TrashIcon,
  EyeSlashIcon,
  CheckCircleIcon,
  UserIcon,
  ShieldIcon,
} from "@phosphor-icons/react";

import CustomText from "../../common/general/CustomText/CustomText";
import { AuditEventType, AuditEvent } from "@axio/graphql/generated/graphql";
import { formatTimeAgo } from "../../../utils/date";

const getEventIcon = (type: AuditEventType) => {
  switch (type) {
    case AuditEventType.CourseCreated:
    case AuditEventType.DegreeCreated:
      return PlusIcon;
    case AuditEventType.CourseUpdated:
    case AuditEventType.DegreeUpdated:
      return PencilSimpleIcon;
    case AuditEventType.CourseDeleted:
    case AuditEventType.DegreeDeleted:
      return TrashIcon;
    case AuditEventType.CoursePublished:
    case AuditEventType.CourseUnpublished:
      return EyeSlashIcon;
    case AuditEventType.CourseEnrollmentAdded:
    case AuditEventType.DegreeEnrollmentAdded:
    case AuditEventType.UserEnrollmentAdded:
      return UserPlusIcon;
    case AuditEventType.CourseEnrollmentRemoved:
    case AuditEventType.DegreeEnrollmentRemoved:
      return UserMinusIcon;
    case AuditEventType.CourseInvitationAdded:
    case AuditEventType.DegreeInvitationAdded:
    case AuditEventType.UserInvitationAdded:
      return UserIcon;
    case AuditEventType.UserInvitationRemoved:
      return UserMinusIcon;
    case AuditEventType.UserCreated:
      return UsersIcon;
    case AuditEventType.UserRolesChanged:
      return ShieldIcon;
    case AuditEventType.CourseAssignedToFaculty:
      return UserPlusIcon;
    case AuditEventType.CourseRemovedFromFaculty:
      return UserMinusIcon;
    default:
      return CheckCircleIcon;
  }
};

const getEventColor = (type: AuditEventType) => {
  switch (type) {
    case AuditEventType.CourseCreated:
    case AuditEventType.DegreeCreated:
      return "#22C55E";
    case AuditEventType.CourseUpdated:
    case AuditEventType.DegreeUpdated:
      return "#1A8E9D";
    case AuditEventType.CourseDeleted:
    case AuditEventType.DegreeDeleted:
      return "#EF4444";
    case AuditEventType.CoursePublished:
      return "#3B82F6";
    case AuditEventType.CourseUnpublished:
      return "#667085";
    case AuditEventType.CourseEnrollmentAdded:
    case AuditEventType.DegreeEnrollmentAdded:
    case AuditEventType.UserEnrollmentAdded:
      return "#913DAF";
    case AuditEventType.CourseEnrollmentRemoved:
    case AuditEventType.DegreeEnrollmentRemoved:
      return "#F97316";
    case AuditEventType.CourseInvitationAdded:
    case AuditEventType.DegreeInvitationAdded:
    case AuditEventType.UserInvitationAdded:
      return "#913DAF";
    case AuditEventType.UserInvitationRemoved:
      return "#F97316";
    case AuditEventType.UserCreated:
      return "#22C55E";
    case AuditEventType.UserRolesChanged:
      return "#1A8E9D";
    case AuditEventType.CourseAssignedToFaculty:
      return "#913DAF";
    case AuditEventType.CourseRemovedFromFaculty:
      return "#F97316";
    default:
      return "#667085";
  }
};

const formatRoleChanges = (event: AuditEvent) => {
  const { diffKeys, diffBefore, diffAfter } = event;
  const changes: string[] = [];

  const rolesIndex = diffKeys.findIndex((key) => key === "hasRoleRoles");

  if (rolesIndex !== -1) {
    const beforeRoles = diffBefore[rolesIndex] ? JSON.parse(diffBefore[rolesIndex]) : [];
    const afterRoles = diffAfter[rolesIndex] ? JSON.parse(diffAfter[rolesIndex]) : [];

    const extractRoleName = (role: unknown): string => {
      if (typeof role === "string") return role;
      if (role && typeof role === "object") {
        if (Array.isArray(role)) {
          return role
            .map((item) => {
              if (!item || typeof item !== "object") {
                return "Unknown";
              }

              const namedRole = item as { name?: string; role?: string; roleName?: string };
              return namedRole.name || namedRole.role || namedRole.roleName || "Unknown";
            })
            .join(", ");
        }

        const namedRole = role as { name?: string; role?: string; roleName?: string };
        return namedRole.name || namedRole.role || namedRole.roleName || "Unknown";
      }
      return String(role);
    };

    const beforeRoleNames = beforeRoles.map(extractRoleName).filter(Boolean);
    const afterRoleNames = afterRoles.map(extractRoleName).filter(Boolean);

    const addedRoles = afterRoleNames.filter((role: string) => !beforeRoleNames.includes(role));
    if (addedRoles.length > 0) {
      changes.push(`added roles: ${addedRoles.join(", ")}`);
    }

    const removedRoles = beforeRoleNames.filter((role: string) => !afterRoleNames.includes(role));
    if (removedRoles.length > 0) {
      changes.push(`removed roles: ${removedRoles.join(", ")}`);
    }
  }

  return changes.length > 0 ? changes.join(", ") : "roles changed";
};

const formatActivityText = (event: AuditEvent) => {
  const { type, actor, subjectCourse, subjectDegree, subjectUser } = event;
  const actorName = actor?.name || "Deleted User";
  const subjectCourseName = subjectCourse?.name || "Course";
  const subjectDegreeName = subjectDegree?.name || "Degree";
  const subjectUserName = subjectUser?.name || "Deleted User";

  switch (type) {
    case AuditEventType.CourseCreated:
      return `${actorName} created course "${subjectCourseName}"`;
    case AuditEventType.CourseUpdated:
      return `${actorName} updated course "${subjectCourseName}"`;
    case AuditEventType.CourseDeleted:
      return `${actorName} deleted course "${subjectCourseName}"`;
    case AuditEventType.CoursePublished:
      return `Course "${subjectCourseName}" was published by ${actorName}`;
    case AuditEventType.CourseUnpublished:
      return `Course "${subjectCourseName}" was unpublished by ${actorName}`;
    case AuditEventType.CourseEnrollmentAdded:
      return `${actorName} enrolled in course "${subjectCourseName}"`;
    case AuditEventType.CourseEnrollmentRemoved:
      return `${subjectUserName} was removed from course "${subjectCourseName}" by ${actorName}`;
    case AuditEventType.CourseInvitationAdded:
      return `${subjectUserName} was invited to course "${subjectCourseName}" by ${actorName}`;
    case AuditEventType.CourseAssignedToFaculty:
      return `${actorName} assigned course "${subjectCourseName}" to faculty "${subjectUserName}"`;
    case AuditEventType.CourseRemovedFromFaculty:
      return `${actorName} removed course "${subjectCourseName}" from faculty "${subjectUserName}"`;
    case AuditEventType.DegreeCreated:
      return `${actorName} created degree "${subjectDegreeName}"`;
    case AuditEventType.DegreeUpdated:
      return `${actorName} updated degree "${subjectDegreeName}"`;
    case AuditEventType.DegreeDeleted:
      return `${actorName} deleted degree "${subjectDegreeName}"`;
    case AuditEventType.DegreeEnrollmentAdded:
      return `${actorName} enrolled in degree "${subjectDegreeName}"`;
    case AuditEventType.DegreeEnrollmentRemoved:
      return `${subjectUserName} was removed from degree "${subjectDegreeName} " by ${actorName}`;
    case AuditEventType.DegreeInvitationAdded:
      return `${subjectUserName} was invited to degree "${subjectDegreeName}" by ${actorName}`;
    case AuditEventType.UserInvitationRemoved:
      return `${subjectUserName} was removed from invitation by ${actorName}`;
    case AuditEventType.UserCreated:
      return `New user "${subjectUserName}" was created by ${actorName}`;
    case AuditEventType.UserRolesChanged: {
      const roleChanges = formatRoleChanges(event);
      return `${actorName} changed roles for "${subjectUserName}": ${roleChanges}`;
    }
    case AuditEventType.UserEnrollmentAdded:
      return `${actorName} was enrolled`;
    case AuditEventType.UserInvitationAdded:
      return `${actorName} invited user "${subjectUserName}"`;
    default:
      return `${actorName} performed activity "${type}"`;
  }
};

type AdminRecentActivityProps = {
  events: AuditEvent[];
};

const AdminRecentActivity: React.FC<AdminRecentActivityProps> = ({ events }) => {
  if (events.length === 0) {
    return null;
  }

  return (
    <View style={styles.container}>
      <View style={styles.activityHeader}>
        <CustomText text="Recent Activity" size="l" weight="bold" style={styles.activityTitle} />
      </View>
      <View style={styles.activityList}>
        {events.slice(0, 25).map((event, index) => {
          const IconComponent = getEventIcon(event.type);
          const iconColor = getEventColor(event.type);
          const isLastItem = index === Math.min(events.length - 1, 4);

          return (
            <View key={event.id} style={[styles.activityItem, isLastItem && styles.lastActivityItem]}>
              <View style={[styles.activityIcon, { backgroundColor: iconColor }]}>
                <IconComponent size={16} color="#FFFFFF" />
              </View>
              <View style={styles.activityContent}>
                <CustomText
                  text={formatActivityText(event as AuditEvent)}
                  size="s"
                  weight="medium"
                  style={styles.activityText}
                  useTranslationText={false}
                />
                <CustomText
                  text={formatTimeAgo(event.occurredAt)}
                  size="xs"
                  weight="normal"
                  style={styles.activityTime}
                  useTranslationText={false}
                />
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: "#FCFCFD",
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#EAECF0",
    marginBottom: 32,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
  },
  activityHeader: {
    padding: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  activityTitle: {
    color: "#101828",
  },
  activityList: {
    padding: 0,
  },
  activityItem: {
    paddingVertical: 20,
    paddingHorizontal: 24,
    borderBottomWidth: 1,
    borderBottomColor: "#F2F4F7",
    flexDirection: "row",
    alignItems: "center",
    gap: 16,
  },
  lastActivityItem: {
    borderBottomWidth: 0,
  },
  activityIcon: {
    width: 32,
    height: 32,
    borderRadius: 8,
    justifyContent: "center",
    alignItems: "center",
  },
  activityContent: {
    flex: 1,
  },
  activityText: {
    color: "#667085",
  },
  activityTime: {
    color: "#667085",
  },
});

export default AdminRecentActivity;
